import type { Evidence, ProblemsResponse } from "@central-hub/contracts";
import { useEffect, useState } from "react";
import { api } from "./api";
import type { Session } from "./auth";
import { clockSeconds } from "./format";
import { Icon } from "./icons";
import { type ProblemWindow, phaseText, problemHeadline, STATUS_LABEL } from "./model";

/** Loads and shows what went wrong with one check in a window (a chart bucket, an incident). */
export function ProblemPanel({
  target,
  window,
  session,
  onClose,
}: {
  target: string;
  window: ProblemWindow;
  session: Session;
  onClose(): void;
}) {
  const [data, setData] = useState<ProblemsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { check, from, to } = window;

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    (async () => {
      try {
        const token = await session.getToken();
        if (!token) throw new Error("session expired");
        const body = await api.problems(token, target, check, from, to);
        if (!cancelled) setData(body);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "could not load");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session, target, check, from, to]);

  return (
    <section className="problems" aria-label={`What happened: ${check}, ${window.label}`}>
      <header className="problems-head">
        <h4>
          What happened <span className="muted">· {window.label}</span>
        </h4>
        <button type="button" className="iconbtn" onClick={onClose} aria-label="Close">
          <Icon name="close" />
        </button>
      </header>
      {error && (
        <p className="summary-warn" role="alert">
          Couldn’t load: {error}
        </p>
      )}
      {!data && !error && <p className="muted">Loading…</p>}
      {data && <ProblemView data={data} />}
    </section>
  );
}

/** The loaded answer; pure, so it renders in tests without a hub. */
export function ProblemView({ data }: { data: ProblemsResponse }) {
  return (
    <div className="problems-body">
      <p className="problems-headline">
        {problemHeadline(data.results.length, data.total, data.truncated)}
      </p>

      {data.results.length > 0 && (
        <ul className="problem-results">
          {data.results.map((r) => {
            // Timeout messages already say how far the request got.
            const phases = r.message?.includes("TCP ") ? null : phaseText(r.data);
            return (
              <li key={`${r.observedAt}${r.check}`} data-status={r.status}>
                <Icon name={r.status} label={STATUS_LABEL[r.status]} />
                <span className="num">{clockSeconds(r.observedAt)}</span>
                <span className="problem-msg">
                  {r.message ?? STATUS_LABEL[r.status]}
                  {phases && <span className="muted num"> · {phases}</span>}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {data.related.length > 0 && (
        <div className="problem-related">
          <h5 className="eyebrow">Also failing at the same time</h5>
          <ul>
            {data.related.map((p) => (
              <li key={`${p.target}${p.check}`} data-status={p.status}>
                <Icon name={p.status} label={STATUS_LABEL[p.status]} />
                <a href={`#/target/${encodeURIComponent(p.target)}`}>{p.target}</a>
                <span className="mono muted">{p.check}</span>
                <span className="num muted">
                  from {clockSeconds(p.firstAt)}
                  {p.count > 1 ? ` · ${p.count}×` : ""}
                </span>
                {p.message && <span className="ellipsis muted">{p.message}</span>}
              </li>
            ))}
          </ul>
          {data.related.length >= 3 && (
            <p className="muted problem-hint">
              Many checks failing at once usually points at something they share (a node, the
              network, the hub itself) rather than at each site.
            </p>
          )}
        </div>
      )}

      <div className="problem-evidence">
        <h5 className="eyebrow">Captured when it started failing</h5>
        {data.evidence.length === 0 ? (
          <p className="muted">
            Nothing captured. Evidence is gathered the moment a check goes from up to failing, for
            targets an investigator (such as the kubernetes module) knows about.
          </p>
        ) : (
          data.evidence.map((e) => (
            <EvidenceBlock key={`${e.source}${e.capturedAt}`} evidence={e} />
          ))
        )}
      </div>
    </div>
  );
}

function EvidenceBlock({ evidence }: { evidence: Evidence }) {
  return (
    <div className="evidence">
      <p className="muted num evidence-meta">
        {clockSeconds(evidence.capturedAt)} · {evidence.source} · after the failure at{" "}
        {clockSeconds(evidence.observedAt)}
      </p>
      {evidence.error && <p className="summary-warn">Investigation incomplete: {evidence.error}</p>}
      {evidence.items.map((item) => {
        const flagged = item.status === "down" || item.status === "degraded";
        return (
          <details
            key={item.title}
            className="evidence-item"
            data-status={item.status}
            open={flagged}
          >
            <summary>
              {item.status ? (
                <Icon name={item.status} label={STATUS_LABEL[item.status]} />
              ) : (
                <span className="evidence-dot" aria-hidden="true" />
              )}
              <span className="evidence-title">{item.title}</span>
              {item.summary && <span className="evidence-summary">{item.summary}</span>}
            </summary>
            {item.lines && item.lines.length > 0 && (
              <pre className="evidence-lines">{item.lines.join("\n")}</pre>
            )}
          </details>
        );
      })}
    </div>
  );
}
